// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Il primo giorno con LabThree"
// Centro assistenza → guida per OPERATORI.
//
// Presupposto di partenza (concordato): l'operatore ha ricevuto il PIN
// dall'amministratore e non sa nient'altro, nemmeno cosa sia LabThree.
//
// Le schermate sono RICOSTRUITE, non fotografate: vedi la nota in atomi.js.
// Tutte le etichette fra virgolette sono copiate dal codice dell'app. Se una
// non corrisponde piu' a quello che si vede a schermo, va corretta qui.
//
// Riferimenti nel codice, per chi dovra' aggiornare questa guida:
//   Login .................. App.js, componente Login
//   Home operatore ......... ui/shell.js, HomeOperatore
//   Voci del menu .......... ui/shell.js, buildSidebarItems
//   Attività da eseguire ... App.js, sezione 'attivita'
//   Registra valore ........ App.js, componente FormValore
//   Correggi monitoraggio .. App.js, componente ModalCorreggi (dentro Storico)
//   Icona sul telefono ..... public/index.html + public/manifest.json
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import { Chip, CatTile } from '../../ui/components';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, Segmenti, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'g0', label: 'Prima di iniziare' },
  { id: 'g1', label: '1. Entrare' },
  { id: 'g2', label: "2. Com'è fatta la finestra" },
  { id: 'g3', label: '3. Il primo controllo' },
  { id: 'g4', label: '4. Se il valore è fuori soglia' },
  { id: 'g5', label: '5. Se il controllo non serve' },
  { id: 'g6', label: '6. Ho sbagliato a scrivere' },
  { id: 'g7', label: "7. L'app sul telefono" },
  { id: 'g8', label: 'Le trappole' },
  { id: 'g9', label: 'Domande frequenti' },
];

// ─────── Pezzi riutilizzati nelle ricostruzioni ──────────────────────────────

const BtnFinto = ({ children, kind = 'neutro' }) => {
  const c = {
    verde:  { bg: LT.verde,  fg: '#fff', bd: LT.verde },
    rosso:  { bg: LT.rosso,  fg: '#fff', bd: LT.rosso },
    neutro: { bg: LT.n0,     fg: LT.n700, bd: LT.n300 },
  }[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      padding: '7px 12px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

const CampoFinto = ({ etichetta, valore, placeholder, larghezza }) => (
  <div style={{ width: larghezza || '100%' }}>
    <div style={{
      fontSize: 11, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '8px 10px',
      background: LT.n0, fontSize: 13.5,
      color: valore ? LT.n900 : LT.n400,
    }}>{valore || placeholder}</div>
  </div>
);

// ─────── La guida ────────────────────────────────────────────────────────────

export default function PrimoGiorno() {
  return (
    <GuidaLayout
      titolo="Il primo giorno con LabThree"
      sottotitolo="Entrare, capire cosa hai davanti e registrare il tuo primo controllo. Non serve sapere niente in anticipo: basta il PIN che ti ha dato il tuo responsabile."
      durata="~ 12 minuti"
      destinatario="Per operatori"
      sezioni={SEZIONI}
    >

      {/* ── g0 ─────────────────────────────────────────────────────────── */}
      <H id="g0">Prima di iniziare</H>

      <P>
        LabThree è il quaderno dei controlli della tua azienda, in versione digitale. Le
        temperature dei frigoriferi, le pulizie, i controlli sulle attrezzature: tutto quello
        che prima si scriveva sui fogli appesi al muro adesso si registra qui. Non serve
        installare niente sul computer, si apre col browser come un sito.
      </P>
      <P>
        A te serve per due cose: <Strong>vedere cosa devi controllare oggi</Strong> e
        <Strong> registrare quello che hai misurato</Strong>. Il resto — decidere quali
        controlli si fanno e ogni quanto — lo imposta l'amministratore, e non è affare tuo.
      </P>

      <Callout kind="info" titolo="Cosa ti serve avere in mano">
        L'<Strong>indirizzo della tua azienda</Strong>, che finisce col nome della tua ditta
        (per esempio <Code>app.labthree.it/CASEIFICIO</Code>), e il tuo{' '}
        <Strong>PIN personale</Strong>. Li dà l'amministratore. Se non li hai, fermati qui e
        chiedili: senza quelli non si entra, e non c'è modo di recuperarli da soli.
      </Callout>

      <P>
        Il PIN è tuo e non si presta. Tutto quello che registri resta scritto col tuo nome, e
        se un giorno arriva un controllo l'ispettore vede chi ha misurato cosa. Non è una
        sorveglianza: è esattamente il motivo per cui questi registri hanno valore.
      </P>

      {/* ── g1 ─────────────────────────────────────────────────────────── */}
      <H id="g1" cat="formazione">1. Entrare</H>

      <P>
        Apri il browser e scrivi l'indirizzo della tua azienda. Non cercare "LabThree" su
        Google: ti porterebbe al sito di presentazione, non alla tua azienda. Ogni azienda ha
        un indirizzo diverso, ed è quello che devi usare.
      </P>

      <MockFrame url="app.labthree.it/CASEIFICIO" markers={[
        { n: 1, top: '58%', left: '50%', titolo: 'Il campo del PIN',
          testo: 'Le cifre restano coperte mentre scrivi. È normale, serve a non farle leggere a chi ti passa accanto.' },
        { n: 2, top: '80%', left: '50%', titolo: 'Accedi',
          testo: 'Premi qui, oppure premi Invio sulla tastiera. Per un attimo il pulsante scrive "Accesso in corso…".' },
      ]}>
        <Schermo style={{ padding: '34px 18px', textAlign: 'center' }}>
          <div style={{
            width: 54, height: 54, borderRadius: LT.r.lg, margin: '0 auto 14px',
            background: LT.gradSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Ico d={I.shield} size={26} stroke={LT.blu} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: LT.navy }}>Caseificio Val di Latte</div>
          <div style={{ fontSize: 13, color: LT.n500, marginTop: 3, marginBottom: 22 }}>
            Controllo HACCP e qualità
          </div>
          <div style={{ maxWidth: 260, margin: '0 auto', textAlign: 'left' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: LT.n700, marginBottom: 6 }}>
              Inserisci il tuo PIN
            </div>
            <div style={{
              border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '10px 12px',
              background: LT.n0, fontSize: 18, letterSpacing: 6, color: LT.n400,
            }}>••••••</div>
            <div style={{
              marginTop: 12, padding: '10px 12px', borderRadius: LT.r.md,
              background: LT.blu, color: '#fff', fontSize: 14, fontWeight: 700,
              textAlign: 'center',
            }}>Accedi</div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Se hai sbagliato le cifre compare <Strong>"PIN non valido"</Strong>. Riprova con
        calma, ma non a raffica: dopo <Strong>cinque tentativi sbagliati</Strong> l'accesso si
        chiude per un quarto d'ora e leggerai "Troppi tentativi falliti su questa azienda.
        Riprova tra 15 minuti."
      </P>

      <Callout kind="warn" titolo="Il blocco è dell'azienda, non tuo">
        Quei cinque tentativi si contano <Strong>per azienda</Strong>, non per persona. Se un
        collega prima di te ha sbagliato il PIN cinque volte, tu ti trovi bloccato senza aver
        fatto niente. Non è un guasto: aspetta un quarto d'ora, oppure avvisa
        l'amministratore. Se compare "Accesso bloccato per questa azienda" il blocco è più
        lungo e serve che sia lui a intervenire.
      </Callout>

      <P>
        Se invece leggi <Strong>"Accesso non autorizzato"</Strong> con la frase "Il codice
        azienda nell'URL non è valido", il PIN non c'entra: hai sbagliato l'indirizzo. Ricontrolla
        la parte dopo la barra.
      </P>

      {/* ── g2 ─────────────────────────────────────────────────────────── */}
      <H id="g2" cat="produzione">2. Com'è fatta la finestra</H>

      <P>
        Appena entri trovi il tuo nome in alto e sotto i riquadri di quello che puoi fare. A
        sinistra c'è il menu, sempre lo stesso in ogni schermata. Sul telefono il menu è
        chiuso e si apre col pulsante <Tasto>☰</Tasto> in alto.
      </P>

      <MockFrame url="app.labthree.it/CASEIFICIO" markers={[
        { n: 1, top: '30%', left: '13%', titolo: 'Il menu',
          testo: 'Le voci scritte in grigio chiaro non si aprono: vuol dire che quel permesso non ti è stato dato. Passandoci sopra col mouse leggi "Non abilitato per il tuo profilo".' },
        { n: 2, top: '52%', left: '58%', titolo: 'Le tue attività',
          testo: 'È il riquadro da cui parti ogni mattina: porta alla lista dei controlli da fare.' },
        { n: 3, top: '90%', left: '13%', titolo: 'Esci',
          testo: 'In fondo al menu. Attenzione: esce subito, senza chiedere conferma.' },
      ]}>
        <Schermo style={{ padding: 0, display: 'flex', minHeight: 260 }}>
          {/* menu */}
          <div style={{
            width: 168, flexShrink: 0, background: LT.n0,
            borderRight: `1px solid ${LT.n150}`, padding: '14px 10px',
            display: 'flex', flexDirection: 'column',
          }}>
            {[
              ['home', 'Home', false],
              ['list', 'Attività', false],
              ['shield', 'Verifiche', true],
              ['history', 'Storico', false],
              ['folder', 'Documenti', false],
              ['alert', 'Non conformità', false],
            ].map(([ic, label, off]) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 8px', borderRadius: LT.r.sm, marginBottom: 2,
                fontSize: 12.5, fontWeight: 600,
                color: off ? LT.n400 : LT.n700,
                background: label === 'Home' ? LT.bluSoft : 'transparent',
                opacity: off ? 0.7 : 1,
              }}>
                <Ico d={I[ic]} size={14} stroke={off ? LT.n400 : LT.n600} />
                {label}
              </div>
            ))}
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px dashed ${LT.n150}` }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: LT.n400,
                textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 8px 4px',
              }}>Supporto</div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px',
                fontSize: 12.5, fontWeight: 600, color: LT.n700,
              }}>
                <Ico d={I.info} size={14} stroke={LT.n600} />Centro assistenza
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px',
              fontSize: 12.5, fontWeight: 600, color: LT.n600,
              borderTop: `1px solid ${LT.n150}`, paddingTop: 10,
            }}>
              <Ico d={I.logout} size={14} stroke={LT.n500} />Esci
            </div>
          </div>

          {/* corpo */}
          <div style={{ flex: 1, padding: 18 }}>
            <div style={{ fontSize: 11.5, color: LT.n500, fontWeight: 600 }}>
              lunedì 17 agosto
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: LT.navy, margin: '2px 0 2px' }}>
              Ciao Luca
            </div>
            <div style={{ fontSize: 12.5, color: LT.n500, marginBottom: 16 }}>
              Caseificio Val di Latte
            </div>
            <div style={{
              fontSize: 10.5, fontWeight: 700, color: LT.n500,
              textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8,
            }}>Operatività</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                ['list', 'Le mie attività', 'Monitoraggi da eseguire', false],
                ['shield', 'Le mie verifiche', 'Non abilitato per il tuo profilo', true],
                ['history', 'Storico monitoraggi', 'Registro storico attività', false],
                ['folder', 'Documenti HACCP', 'Procedure, modulistica, tecnici', false],
              ].map(([ic, tit, sot, off]) => (
                <div key={tit} style={{
                  background: LT.n0, border: `1px solid ${LT.n150}`, borderRadius: LT.r.md,
                  padding: '10px 11px', opacity: off ? 0.6 : 1,
                }}>
                  <Ico d={I[ic]} size={16} stroke={off ? LT.n400 : LT.blu} />
                  <div style={{
                    fontSize: 13, fontWeight: 700, marginTop: 5,
                    color: off ? LT.n500 : LT.navy,
                  }}>{tit}</div>
                  <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>{sot}</div>
                </div>
              ))}
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="warn" titolo='"Il pulsante Attività è grigio"'>
        È la domanda che arriva più spesso, e non è un guasto. Per registrare i monitoraggi
        serve il permesso di <Strong>monitoratore</Strong>; per le verifiche quello di{' '}
        <Strong>verificatore</Strong>. Li assegna l'amministratore da <Code>Organico</Code>.
        Se la voce è grigia, chiedi a lui: a te non c'è niente da fare.
      </Callout>

      {/* ── g3 ─────────────────────────────────────────────────────────── */}
      <H id="g3" cat="temperature">3. Il primo controllo</H>

      <P>
        Apri <Tasto>Le mie attività</Tasto> dalla home, oppure <Tasto>Attività</Tasto> dal
        menu. La schermata si chiama <Strong>"Attività da eseguire"</Strong> e mostra solo
        quello che ti resta da fare, diviso in <Strong>"In ritardo"</Strong> e{' '}
        <Strong>"In scadenza"</Strong>.
      </P>

      <MockFrame url="app.labthree.it/CASEIFICIO" markers={[
        { n: 1, top: '11%', left: '78%', titolo: 'Classica o Settimanale',
          testo: 'Due modi di vedere la stessa cosa. "Settimanale" è una griglia con i giorni in colonna: comoda per capire a colpo d\'occhio cosa manca.' },
        { n: 2, top: '75%', left: '62%', titolo: 'Conforme / Non conforme',
          testo: 'La scelta si fa qui, prima di scrivere il numero. "Conforme" vuol dire che il valore che hai misurato è nella norma.' },
        { n: 3, top: '75%', left: '87%', titolo: 'N.A.',
          testo: 'Per quando il controllo oggi non ha senso. Ne parliamo al punto 5.' },
      ]}>
        <Schermo>
          <SchermoTitolo
            titolo="Attività da eseguire"
            sottotitolo="lunedì 17 agosto · Caseificio Val di Latte"
            destra={<Segmenti voci={['Classica', 'Settimanale']} attivo="Classica" />}
          />
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12,
            fontSize: 11.5, color: LT.n600,
          }}>
            <Chip kind="neutro" size="sm">Tutti</Chip>
            <Chip kind="neutro" size="sm">Temperature</Chip>
            <Chip kind="neutro" size="sm">Pulizie</Chip>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 11px', borderRadius: LT.r.md, fontSize: 11.5, fontWeight: 700,
              background: LT.bluSoft, color: LT.blu,
            }}>
              <Ico d={I.clock} size={13} stroke={LT.blu} />Avvia sessione
            </span>
          </div>
          <div style={{
            fontSize: 10.5, fontWeight: 800, color: LT.blu,
            textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7,
          }}>In scadenza</div>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
            padding: 13, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <CatTile cat="temperature" size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: LT.navy }}>
                Controllo temperature
              </div>
              <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
                Cella frigo latte crudo — Locale ricevimento
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                <Chip kind="neutro" size="sm">Min: 0</Chip>
                <Chip kind="neutro" size="sm">Max: 4</Chip>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', gap: 5 }}>
                <BtnFinto kind="verde">Conforme</BtnFinto>
                <BtnFinto kind="rosso">Non conforme</BtnFinto>
              </div>
              <BtnFinto>N.A. — non applicabile oggi</BtnFinto>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        I due numeri <Strong>Min</Strong> e <Strong>Max</Strong> sono i limiti decisi dal tuo
        responsabile: se la misura sta dentro quei due numeri il controllo è conforme, se sta
        fuori non lo è. Guardali <em>prima</em> di scegliere il pulsante.
      </P>

      <Passo n={1} titolo="Misura, poi premi Conforme">
        <P style={{ margin: '6px 0' }}>
          Prima leggi il termometro, poi apri la finestra. Se il valore è nella norma premi{' '}
          <Tasto>Conforme</Tasto>.
        </P>
      </Passo>

      <Passo n={2} titolo="Scrivi il numero">
        <P style={{ margin: '6px 0' }}>
          Si apre la finestra <Strong>"Registra valore"</Strong>, con la scritta "Esito
          conforme" sotto il titolo. Nel campo <Strong>"Valore misurato"</Strong> metti quello
          che hai letto. Usa il punto o la virgola per i decimali, come ti viene.
        </P>
      </Passo>

      <Passo n={3} titolo="Conferma">
        <P style={{ margin: '6px 0' }}>
          Premi <Tasto>Conferma</Tasto>. L'attività <Strong>sparisce dalla lista</Strong>:
          è il segno che è andata a posto. Non l'hai perduta, la ritrovi in{' '}
          <Code>Storico</Code>.
        </P>
      </Passo>

      <MockFrame didascalia="La finestra di registrazione. I campi Data e Ora ci sono finché non avvii una sessione: se ne hai una attiva, al loro posto compare un riquadro che ricorda la data e l'ora che hai fissato tu.">
        <Schermo style={{ padding: 22, background: LT.n100 }}>
          <div style={{
            maxWidth: 340, margin: '0 auto', background: LT.n0,
            borderRadius: LT.r.lg, border: `1px solid ${LT.n200}`, padding: 16,
            boxShadow: LT.sh2,
          }}>
            <div style={{ fontSize: 16.5, fontWeight: 800, color: LT.navy }}>Registra valore</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: LT.verde, marginTop: 2 }}>
              Esito conforme
            </div>
            <div style={{
              margin: '12px 0', padding: '9px 11px', background: LT.n50,
              border: `1px solid ${LT.n150}`, borderRadius: LT.r.md, fontSize: 12.5,
            }}>
              <div style={{ fontWeight: 700, color: LT.n800 }}>Cella frigo latte crudo</div>
              <div style={{ color: LT.n500, marginTop: 2 }}>
                Locale ricevimento · Temperature · Controllo temperature
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 7 }}>
                <Chip kind="neutro" size="sm">Min: 0</Chip>
                <Chip kind="neutro" size="sm">Max: 4</Chip>
              </div>
            </div>
            <CampoFinto etichetta="Valore misurato" valore="2,5" />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <CampoFinto etichetta="Data (opzionale)" placeholder="17/08/2026" larghezza="50%" />
              <CampoFinto etichetta="Ora (opzionale)" placeholder="08:30" larghezza="50%" />
            </div>
            <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end', marginTop: 16 }}>
              <BtnFinto>Annulla</BtnFinto>
              <span style={{
                padding: '7px 14px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 700,
                background: LT.blu, color: '#fff',
              }}>Conferma</span>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="success" titolo="Non devi firmare niente">
        Molti cercano un campo per la firma e non lo trovano. Non c'è, ed è giusto così: la
        registrazione porta il tuo nome perché sei entrato col tuo PIN. La firma vera e propria
        la carica l'amministratore una volta sola, e compare sulle stampe.
      </Callout>

      <P>
        Data e ora si compilano da sole con il momento in cui registri. Servono solo se stai
        mettendo a posto un controllo fatto prima, per esempio stamattina alle sei quando non
        avevi il telefono in mano.
      </P>

      <P style={{ fontSize: 17, fontWeight: 700, color: LT.n900, marginTop: 28 }}>
        La sessione di registrazione
      </P>
      <P>
        Se i controlli da mettere a posto sono venti, riscrivere ogni volta la stessa data e la
        stessa ora è una perdita di tempo. Per questo in alto a destra nella schermata delle
        attività c'è <Tasto>Avvia sessione</Tasto>: fissi <Strong>una volta</Strong> data e ora
        — la finestra si chiama "Avvia sessione di registrazione" — e da quel momento{' '}
        <Strong>tutte le registrazioni che fai usano quell'orario</Strong>, senza più chiedertelo.
        Vale anche per i N.A.
      </P>
      <P>
        Mentre è attiva vedi in cima un riquadro azzurro <Strong>"Sessione attiva"</Strong> con
        la data e l'ora scelte, e accanto <Tasto>Termina</Tasto> per chiuderla.
      </P>

      <Callout kind="info" titolo="La sessione è tua e di nessun altro">
        La avvii tu, per te, e non tocca quello che fanno i colleghi: ognuno può averne una sua,
        con orari diversi, nello stesso momento. Non è una funzione del responsabile e non
        imposta l'ora "del gruppo".
      </Callout>

      <Callout kind="warn" titolo="Ricordati di terminarla">
        Finché la sessione è attiva, <Strong>ogni</Strong> registrazione porta l'orario che hai
        fissato — anche quelle che stai facendo adesso in tempo reale. Se hai finito di
        recuperare l'arretrato, premi <Tasto>Termina</Tasto>, altrimenti ti ritrovi controlli
        di mezzogiorno segnati alle sei del mattino. La sessione si perde comunque da sé se
        ricarichi la pagina o esci dalla schermata delle attività: non resta accesa da un
        giorno all'altro.
      </Callout>

      {/* ── g4 ─────────────────────────────────────────────────────────── */}
      <H id="g4" cat="pulizie">4. Se il valore è fuori soglia</H>

      <P>
        Qui LabThree si comporta in un modo che sorprende, e conviene saperlo prima. Se premi{' '}
        <Tasto>Conforme</Tasto> e poi scrivi un numero fuori dai limiti, il programma{' '}
        <Strong>si rifiuta di registrare</Strong> e ti dice:
      </P>

      <Callout kind="warn">
        "Valore fuori dai limiti (min: 0, max: 4) — non puoi registrare come CONFORME"
      </Callout>

      <P>
        Non è un capriccio: un registro che dichiara conforme una temperatura fuori norma non
        vale niente, e in caso di controllo è peggio che non averlo. Funziona anche al
        contrario: se premi <Tasto>Non conforme</Tasto> e il numero è invece dentro i limiti,
        leggerai "Valore nei limiti — non puoi registrare come NON CONFORME".
      </P>

      <P>
        La strada giusta è chiudere la finestra, premere <Tasto>Non conforme</Tasto> e
        riscrivere il valore. A quel punto si apre la finestra <Strong>"Non conformità"</Strong>,
        con la descrizione già compilata: cosa hai misurato, dove, e di quanto ha sforato. A te
        resta da dire <Strong>cosa hai fatto per rimediare</Strong> — hai spostato la merce,
        chiamato il tecnico, buttato il prodotto.
      </P>

      <Callout kind="info" titolo="Una non conformità non è una colpa">
        Registrarla è la cosa giusta, non un'ammissione di negligenza. Un frigorifero che va
        fuori temperatura succede a tutti; quello che un ispettore vuole vedere è che
        l'avete notato e che avete fatto qualcosa. Un registro senza nemmeno una non
        conformità in due anni è quello che insospettisce.
      </Callout>

      {/* ── g5 ─────────────────────────────────────────────────────────── */}
      <H id="g5">5. Se il controllo non serve</H>

      <P>
        Capita che un controllo in programma oggi non abbia senso: la cella è spenta, la
        macchina non è stata usata, il reparto è chiuso. In quel caso non inventare un numero e
        non lasciare il controllo in bianco. Premi{' '}
        <Tasto>N.A. — non applicabile oggi</Tasto>.
      </P>
      <P>
        Compare la conferma "Attività marcata come N.A." e il controllo esce dalla lista. Non
        ti chiede una spiegazione scritta, ma resta registrato che quel giorno era non
        applicabile — che è diverso, e molto meglio, di una casella vuota.
      </P>

      <Callout kind="warn" titolo="Non usare N.A. per rimandare">
        N.A. vuol dire "oggi questo controllo non andava fatto", non "lo faccio dopo". Se il
        controllo va fatto e non hai ancora avuto tempo, lascialo dov'è: domani lo trovi nella
        sezione "In ritardo", che è l'avviso corretto.
      </Callout>

      {/* ── g6 ─────────────────────────────────────────────────────────── */}
      <H id="g6" cat="manutenzioni">6. Ho sbagliato a scrivere</H>

      <P>
        Succede: 2,5 diventa 25. Si può correggere da soli, entro certi limiti, e la
        correzione <Strong>non si fa dalla schermata Attività</Strong> — quel controllo è già
        sparito da lì. Si fa da <Code>Storico</Code>.
      </P>
      <P>
        Trova la riga giusta e premi <Tasto>Correggi</Tasto>. Si apre "Correggi monitoraggio",
        che ti chiede il valore nuovo e una <Strong>motivazione obbligatoria</Strong>: due
        parole bastano, "errore di battitura" va benissimo. Sotto c'è scritto che tutte le
        modifiche vengono tracciate nel log, e non è una minaccia: è la ragione per cui ti è
        permesso correggere invece di dover chiamare qualcuno.
      </P>

      <Callout kind="info" titolo="Se il pulsante Correggi è spento">
        Capita, e il programma ti dice perché: <Strong>passaci sopra col mouse</Strong> e leggi il
        messaggio. Sono tre i casi in cui non si può correggere da soli — se la registrazione
        l'ha fatta un collega e non tu, se un responsabile l'ha già verificata, o se sono passati{' '}
        <Strong>più di 7 giorni</Strong>. In tutti e tre serve un amministratore.
      </Callout>

      {/* ── g7 ─────────────────────────────────────────────────────────── */}
      <H id="g7">7. L'app sul telefono</H>

      <P>
        Il posto naturale per usare LabThree è il telefono, con la mano libera davanti al
        frigorifero. Conviene mettere l'icona sulla schermata Home, così non devi riscrivere
        l'indirizzo ogni volta.
      </P>

      <Passo n={1} titolo="Apri l'indirizzo della tua azienda">
        <P style={{ margin: '6px 0' }}>
          Questo è il passo che sbagliano tutti. L'icona ricorda{' '}
          <Strong>l'ultimo indirizzo aziendale che hai aperto</Strong>: se la crei da una
          pagina qualsiasi, non ha niente da ricordare. Assicurati di vedere il nome della tua
          azienda nella barra dell'indirizzo.
        </P>
      </Passo>
      <Passo n={2} titolo="Su iPhone">
        <P style={{ margin: '6px 0' }}>
          Con Safari, premi il pulsante <Tasto>Condividi</Tasto> in basso — il quadrato con la
          freccia verso l'alto — scorri e scegli <Tasto>Aggiungi a Home</Tasto>. Deve essere
          Safari: da Chrome su iPhone la voce non c'è.
        </P>
      </Passo>
      <Passo n={3} titolo="Su Android">
        <P style={{ margin: '6px 0' }}>
          Con Chrome, premi i tre puntini in alto a destra e scegli{' '}
          <Tasto>Installa app</Tasto> oppure <Tasto>Aggiungi a schermata Home</Tasto>, a
          seconda della versione.
        </P>
      </Passo>

      <P>
        Da quel momento l'icona apre direttamente la tua azienda, a schermo pieno, senza la
        barra del browser. Se la usi in navigazione anonima non funziona: il telefono non ha
        modo di ricordare l'indirizzo.
      </P>

      {/* ── g8 ─────────────────────────────────────────────────────────── */}
      <H id="g8">Le trappole</H>

      <P>
        Le sei cose che fanno alzare il telefono più spesso. Nessuna è un guasto.
      </P>

      <div style={{ margin: '16px 0' }}>
        {[
          ['Il pulsante Attività è grigio',
            'Manca il permesso di monitoratore. Lo dà l\'amministratore da Organico.'],
          ['L\'attività è sparita dopo che l\'ho registrata',
            'È il comportamento giusto: la lista mostra solo quello che resta da fare. La registrazione è in Storico.'],
          ['Esci non chiede conferma',
            'Un tocco e sei fuori, senza domande. Non perdi niente di registrato, ma devi rifare il PIN.'],
          ['Non trovo il tasto indietro',
            'Non c\'è dentro l\'app: per tornare si usa il menu di sinistra, o il tasto indietro del browser.'],
          ['Sono bloccato e non ho sbagliato niente',
            'Il blocco dopo cinque PIN sbagliati vale per tutta l\'azienda. Aspetta quindici minuti.'],
          ['Le registrazioni hanno tutte l\'ora sbagliata',
            'Probabilmente è rimasta attiva una sessione: finché c\'è, ogni registrazione prende l\'ora che avevi fissato. Premi Termina nel riquadro azzurro.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── g9 ─────────────────────────────────────────────────────────── */}
      <H id="g9">Domande frequenti</H>

      <FAQ q="Ho dimenticato il PIN. Come lo recupero?">
        Da solo non puoi, e non è previsto nessun "PIN dimenticato". Lo reimposta
        l'amministratore da <Code>Organico</Code>. È voluto: un registro sanitario in cui
        ognuno può rigenerarsi le credenziali da solo non avrebbe alcun valore.
      </FAQ>

      <FAQ q="Posso registrare un controllo di ieri?">
        Sì. Nella finestra "Registra valore" ci sono i campi <Strong>Data</Strong> e{' '}
        <Strong>Ora</Strong>: normalmente si riempiono da soli con l'adesso, ma li puoi
        cambiare. È il modo corretto di mettere a posto un controllo fatto quando non avevi
        l'app sottomano.
      </FAQ>

      <FAQ q="Devo registrare qualcosa anche se non ho fatto niente?">
        Se il controllo era in programma, sì: o lo registri, o lo segni N.A. se davvero non
        andava fatto. Le caselle vuote sono l'unica cosa che in un'ispezione non si può
        spiegare.
      </FAQ>

      <FAQ q="Che differenza c'è fra Attività e Verifiche?">
        L'attività è il controllo che fai tu sul campo: misuri e registri. La verifica è il
        controllo del controllo, e la fa chi ha il permesso di verificatore — di solito un
        responsabile — per confermare che i monitoraggi siano stati fatti come si deve. Se la
        voce Verifiche è grigia, non è compito tuo.
      </FAQ>

      <FAQ q="Se registro un valore fuori soglia, finisco nei guai?">
        No, e anzi è esattamente quello che il sistema vuole da te. Chi guarda questi registri
        si aspetta di trovare qualche non conformità con la sua azione correttiva: è il segno
        che i controlli si fanno per davvero. Un registro perfetto per due anni è quello che
        fa venire dei dubbi.
      </FAQ>

      <FAQ q="Serve internet?">
        Sì, LabThree lavora in collegamento col server. Se la connessione manca nel punto dove
        stai misurando, scrivi il valore su un foglio e registralo appena torni in copertura,
        usando i campi Data e Ora per indicare il momento vero della misura.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Sai fare tutto quello che serve per una giornata di lavoro. Quando ti capiterà una non
          conformità da seguire fino in fondo, o dovrai cercare una registrazione vecchia nello
          Storico, in questo Centro assistenza trovi le guide dedicate.
        </div>
      </div>

    </GuidaLayout>
  );
}
